import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { filter, switchMap, take } from 'rxjs';
import { BASE_URL } from '@kikkz/shared/config/util';
import { AuthFacade } from '../facade/auth.facade';

/**
 * Determines whether `url` targets the backend identified by `baseUrl`.
 *
 * Uses parsed URLs rather than a string-prefix check so that a host which merely
 * *starts with* `baseUrl` as a string (e.g. `https://api.example.com.evil.example.com`)
 * is correctly rejected — a naive `startsWith` check would leak the Bearer token to it.
 *
 * A match requires:
 * - the same origin (scheme + host + port), and
 * - the request's pathname to start with the base URL's pathname at a segment boundary,
 *   so `${baseUrl}/foo` matches but `${baseUrl}foo` (no separating slash) does not.
 */
export function isBackendRequest(url: string, baseUrl: string): boolean {
  try {
    const requestUrl = new URL(url, globalThis.location?.origin);
    const base = new URL(baseUrl);

    if (requestUrl.origin !== base.origin) {
      return false;
    }

    const basePathname = base.pathname.endsWith('/') ? base.pathname.slice(0, -1) : base.pathname;

    if (!requestUrl.pathname.startsWith(basePathname)) {
      return false;
    }

    const remainder = requestUrl.pathname.slice(basePathname.length);
    return remainder === '' || remainder.startsWith('/');
  } catch {
    return false;
  }
}

/**
 * Attaches the OIDC id_token as a Bearer token to backend requests.
 * Requests to other origins (e.g. the identity provider's token endpoint) are left untouched.
 *
 * While auth is still resolving on startup (e.g. tokens being loaded from storage),
 * backend requests are held back until resolution — otherwise requests fired during
 * the first render would go out without a token and 401.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authFacade = inject(AuthFacade);
  const baseUrl = inject(BASE_URL);

  if (!isBackendRequest(req.url, baseUrl)) {
    return next(req);
  }

  const withBearer = (request: HttpRequest<unknown>) => {
    const token = authFacade.idToken();
    return token ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : request;
  };

  if (!authFacade.isLoading()) {
    return next(withBearer(req));
  }

  return authFacade.isLoading$.pipe(
    filter((isLoading) => !isLoading),
    take(1),
    switchMap(() => next(withBearer(req))),
  );
};
